// apps/share-web/src/text-share/words-model.ts
//
// Чистые решения страницы слов: что именно известно про каждую песню сетлиста и
// как её назвать. Ни `document`, ни состояния — только нагрузка на входе.
//
// Модуль отдельный от `words-view.ts` по инварианту роста волны: «новая фича =
// новые модули + тонкая оркестрация». Вид собирает DOM, здесь живут правила, и
// каждое проверяется тестом там, где раскладки нет вовсе.
//
// Нагрузка приходит из сети и по публичному маршруту, поэтому терпимость тут не
// вежливость, а требование: старая ссылка, недописанная песня и мусор в поле не
// должны превращать страницу в белый экран посреди служения. Правило одно —
// «нечем заполнить» рисуется отсутствием строки, а не словом «undefined».

import type { TextSharePayload, TextShareSong } from '@bandway/share-types';
import type { SectionSec } from '../chart/chart-follow.js';
import { gridSections, songMetaLine } from '../chart/chart-song.js';
import { t } from '../ui/i18n.js';

export interface WordsSong {
  /**
   * Место песни в ЭТОМ списке — оно же адрес перехода: у текстовой ссылки нет
   * отдельных шейров на песню, навигация позиционная (см. `SetlistEntry` в
   * chart-fullscreen.ts). Нумерация сплошная даже когда из нагрузки что-то
   * выброшено: дырка в ней означала бы «Next», уводящий в никуда.
   */
  index: number;
  name: string;
  /**
   * Исходная тональность песни, как её записал владелец. Нужна отдельно от
   * `meta`: дропдаун тональности считает 15 подписей ОТ НЕЁ (ядро
   * @bandway/chordpro), а из готовой строки «C · 72 bpm · 4/4» её пришлось бы
   * выковыривать обратно. Пустая — не ошибка: подписи станут просто сдвигами.
   */
  key: string;
  /** «C · 72 bpm · 4/4». Пустая строка — меты у песни нет, и строки не будет. */
  meta: string;
  /**
   * Темп песни; 0 = не задан — та же договорённость, что в `ShareManifest.song`
   * и в самом `TextShareSong`. Едет ОТДЕЛЬНО от `meta` по той же причине, что и
   * `key` выше: `meta` — готовая строка ЧЕЛОВЕКУ, а лид (`leadSeconds`,
   * @bandway/chordpro) — АРИФМЕТИКЕ, и разбирать «C · 72 bpm · 4/4» обратно
   * значило бы парсить собственный вывод.
   */
  bpm: number;
  /** Размер такта песни, «4/4». Тем же числам, что и `bpm`, нужна арифметика. */
  timeSignature: string;
  /**
   * ПОЛНОЕ расписание аранжировки в секундах — все секции, с именами и без.
   *
   * Безымянные здесь не по недосмотру: позиция принадлежит ВСЕЙ аранжировке, а
   * выброшенный проигрыш означал дыру в расписании, в которой ведение застревало
   * подсветкой на прошлой секции. Замер и цифры — в `gridSections`
   * (chart-song.ts). Имя решает ровно одно: найдётся ли для секции блок чарта.
   *
   * Поверхность чтения берёт расписание тогда, когда есть источник позиции: без
   * него ни подсветка, ни строка «сейчас → дальше» сказать ничего не могут. В
   * волне B позиции нет вовсе, поэтому `words-view.ts` передаёт слою пустой
   * список — причина и замер там же.
   */
  sections: SectionSec[];
  /**
   * Когда песня КОНЧАЕТСЯ, в секундах, или null — расписания нет.
   *
   * Конец ПОСЛЕДНЕЙ секции полной сетки — в том числе безымянной: песня с
   * безымянным финалом (обрыв, инструментальный хвост) «кончалась» бы иначе
   * раньше времени, и правило 5 часовой модели («за duration не
   * экстраполируем») останавливало бы ведение на последнем НАЗВАННОМ куске, то
   * есть отнимало бы у музыканта место ровно на концовке.
   */
  durationSec: number | null;
  /** ChordPro ДОСЛОВНО (отбивка авторская). Пустая строка — поля не было. */
  text: string;
  /**
   * Хранимые связи «id секции → заголовок». Без них русский чарт не находит ни
   * одного блока, и лист в зале не следит вовсе: связь выводилась бы по именам,
   * а «Куплет 1» и «Verse 1» — разные строки. `null` — законно: так выглядит
   * ссылка, созданная до того, как поле поехало на провод.
   */
  links: Record<string, string> | null;
  /** Есть ли что читать. Судит по тексту, а не по наличию поля. */
  hasWords: boolean;
}

/**
 * Конец последней секции РАЗОБРАННОЙ сетки.
 *
 * Считается по ней, а не по сырой нагрузке: сетка уже отсеяла записи без годных
 * границ (`gridSections`), и мусор в последнем элементе нагрузки не превращает
 * длительность в null у песни, у которой расписание есть. Пустая сетка —
 * «длительность неизвестна», а не повод уронить страницу.
 */
function lastEndSec(grid: readonly SectionSec[]): number | null {
  return grid.length === 0 ? null : grid[grid.length - 1].endSec;
}

/** Имя сетлиста в шапке. Пустое — заглушка: пустая шапка ничего не говорит. */
export function wordsTitle(payload: TextSharePayload): string {
  const name = typeof payload?.name === 'string' ? payload.name.trim() : '';
  return name !== '' ? name : t('textShare.untitled');
}

/** Нагрузка → песни страницы, в порядке сетлиста. */
export function wordsSongs(payload: TextSharePayload): WordsSong[] {
  const src: unknown[] = Array.isArray(payload?.songs) ? payload.songs : [];
  const out: WordsSong[] = [];
  for (const raw of src) {
    // Не объект — это не песня. Выбрасываем молча: одна битая запись не повод
    // отказать читателю в остальных четырёх.
    if (raw == null || typeof raw !== 'object') continue;
    const song = raw as TextShareSong;
    const name = typeof song.name === 'string' ? song.name.trim() : '';
    // Заглушка та же, что в библиотеке (`library.untitled`): одно слово об одном
    // и том же — песня без имени — не должно звучать на двух поверхностях
    // по-разному.
    const text = typeof song.chart?.text === 'string' ? song.chart.text : '';
    // Терпимо ровно как текст рядом: нагрузка приходит из сети.
    const rawLinks = song.chart?.links;
    let links: Record<string, string> | null = null;
    if (rawLinks && typeof rawLinks === 'object' && !Array.isArray(rawLinks)) {
      const clean: Record<string, string> = {};
      for (const [k, v] of Object.entries(rawLinks as Record<string, unknown>)) {
        if (typeof k === 'string' && k && typeof v === 'string' && v.trim()) clean[k] = v;
      }
      if (Object.keys(clean).length) links = clean;
    }
    // Сетка разбирается ОДИН раз: по ней и следят, и считают длительность —
    // второй разбор был бы вторым правилом о том, где песня кончается.
    const grid = gridSections(song.sections);
    out.push({
      index: out.length,
      name: name !== '' ? name : t('library.untitled'),
      key: typeof song.key === 'string' ? song.key : '',
      meta: songMetaLine(song),
      bpm: typeof song.bpm === 'number' && Number.isFinite(song.bpm) ? song.bpm : 0,
      timeSignature: typeof song.timeSignature === 'string' ? song.timeSignature : '',
      sections: grid,
      durationSec: lastEndSec(grid),
      text,
      links,
      hasWords: text.trim() !== '',
    });
  }
  return out;
}

// ── что страница отвечает часовой модели лайва (волна C) ─────────────────────
//
// Обе функции отдают ФУНКЦИЮ по индексу, а не значение: ровно этой формы просит
// `createLiveClient` (`sectionsOf`, `durationSecOf`), и собирать её в месте
// проводки значило бы держать правило «длительность = конец последней секции» в
// main.ts, где его никто не проверяет.

/**
 * Расписание песни по её месту в нагрузке. Индекс приходит из СЕТИ (в якоре
 * едет `songIdx`), поэтому мимо списка — пустое расписание, а не падение.
 */
export function wordsSectionsOf(songs: readonly WordsSong[]): (songIdx: number) => SectionSec[] {
  return (songIdx) => songs[songIdx]?.sections ?? [];
}

/**
 * Длительность песни — конец её ПОСЛЕДНЕЙ секции, или null.
 *
 * Ею живёт правило 5 модели («за duration не экстраполируем»): без неё позиция
 * ушедшего лидера считалась бы вечно, до самого потолка LIVE_GIVE_UP_MS.
 * Расписания нет — null, и это НЕ ошибка: вести такую песню нечем, а соврать
 * «она кончилась» не лучше, чем экстраполировать за её конец. Почему конец
 * считается по полной сетке, а не по названным секциям — см. `durationSec` выше.
 */
export function wordsDurationSecOf(songs: readonly WordsSong[]): (songIdx: number) => number | null {
  return (songIdx) => songs[songIdx]?.durationSec ?? null;
}
